import { Injectable } from '@angular/core';
import { Client } from '@stomp/stompjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class WebSocketService {
  private client: Client | null = null;
  private topicHandlers = new Map<string, Set<(messageBody: string) => void>>();
  private activeTopicSubscriptions = new Map<string, { unsubscribe: () => void }>();

  subscribe(topic: string, handler: (messageBody: string) => void): () => void {
    const handlers = this.topicHandlers.get(topic) ?? new Set<(messageBody: string) => void>();
    handlers.add(handler);
    this.topicHandlers.set(topic, handlers);

    this.ensureConnected();
    this.ensureTopicSubscription(topic);

    return () => {
      const registeredHandlers = this.topicHandlers.get(topic);
      if (!registeredHandlers) {
        return;
      }

      registeredHandlers.delete(handler);
      if (registeredHandlers.size > 0) {
        return;
      }

      this.topicHandlers.delete(topic);
      this.activeTopicSubscriptions.get(topic)?.unsubscribe();
      this.activeTopicSubscriptions.delete(topic);
    };
  }

  private ensureConnected() {
    if (this.client) {
      return;
    }

    this.client = new Client({
      brokerURL: environment.MESSAGE_BROKER_URL,
      reconnectDelay: 5000,
      heartbeatIncoming: 20000,
      heartbeatOutgoing: 20000,
      onConnect: () => {
        for (const topic of this.topicHandlers.keys()) {
          this.ensureTopicSubscription(topic);
        }
      },
      debug: () => {},
      onWebSocketError: (err) => {
        console.error(err);
      },
      onStompError: (err) => {
        console.error(err);
      }
    });

    this.client.activate();
  }

  private ensureTopicSubscription(topic: string) {
    if (!this.client?.connected || this.activeTopicSubscriptions.has(topic)) {
      return;
    }

    const subscription = this.client.subscribe(topic, (message) => {
      this.topicHandlers.get(topic)?.forEach((handler) => handler(message.body));
    });
    this.activeTopicSubscriptions.set(topic, subscription);
  }

  disconnect() {
    this.activeTopicSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.activeTopicSubscriptions.clear();
    if (this.client && this.client.connected) {
      this.client.forceDisconnect();
    }
    this.client = null;
  }
}
